import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IDataDisplayerPagination } from "../../components/DataDisplayer/DataDisplayer";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { EmptyCategoryModel, ICategoryModel } from "./CategoriesApp";
import { ISearchParam } from "../BaseApp";

interface ICategoriesState {
    categories: ICategoryModel[],
    selectedCategories: ICategoryModel[],
    searchParams: ISearchParam[],
    loading: boolean,
    pagination: IDataDisplayerPagination,
    search?: string
}

const initialState: ICategoriesState = {
    categories: [],
    selectedCategories: [],
    searchParams: [],
    loading: false,
    pagination: {
        count: 0,
        current_page: 0,
        total_pages: 0
    }
}

const categoriesSlice = createSlice({
    name: "categories",
    initialState,
    reducers: {
        updateAllCategories: (state: ICategoriesState, action: PayloadAction<Array<ICategoryModel>>) => {
            state.categories = action.payload
        },
        // addNewCategory: (state: ICategoriesState, action: PayloadAction<ICategoryModel>) => {
        //     let categories = state.categories

        //     if (categories.filter(category => category.id === action.payload.id).length === 0){
        //         state.categories.push(action.payload)
        //     }

        // },
        addNewCategory: (state: ICategoriesState, action: PayloadAction<ICategoryModel>) => {
            const newCategory = action.payload;
        
            // If category already exists anywhere, do nothing
            const alreadyExists = findCategoryById(state.categories, newCategory.id);
            if (alreadyExists) return;
        
            // If it's top-level, just push it
            if (!newCategory.is_sub_category) {
                state.categories.push(newCategory);
                return;
            }
        
            // For subcategories, check if parent exists
            const parentExists = findCategoryById(state.categories, newCategory.category_id);
            if (!parentExists) {
                console.warn("Parent category not found. Cannot add subcategory.");
                return; // or optionally queue for retry if you're syncing later
            }
        
            // Insert using recursive helper
            state.categories = insertOrUpdateCategoryById(state.categories, newCategory);
        },
        deleteCategory: (state: ICategoriesState, action: PayloadAction<{ id: number }>) => {
            // const payload = action.payload
            
            // let categories = state.categories

            // categories = categories.filter(item => item.id !== payload.id)

            // state.categories = categories
            const { id } = action.payload;
            state.categories = deleteCategoryById(state.categories, id);

        },
        // updateCategory: (state: ICategoriesState, action: PayloadAction<ICategoryModel>) => {
        //     let categories = state.categories
            
        //     if (categories && categories.length > 0){
        //         categories.map((category) => {
        //             if (category.id === action.payload.id){

        //                 const locationIndex = categories.findIndex(category => category.id === action.payload.id)
        //                 categories[locationIndex] = action.payload

        //             }
        //         })
        //     }

        //     state.categories = categories

        // },
        updateCategory: (state: ICategoriesState, action: PayloadAction<ICategoryModel>) => {
            const updatedCategory = action.payload;
            state.categories = insertOrUpdateCategoryById(state.categories, updatedCategory);
        },
        updateCategoriesLoadingStatus: (state: ICategoriesState, action: PayloadAction<{ loading: boolean }>) => {
            state.loading = action.payload.loading
        },
        updateCategoriesPagination: (state: ICategoriesState, action: PayloadAction<IDataDisplayerPagination>) => {
            state.pagination = action.payload
        },
        updateCategoriesSearch: (state: ICategoriesState, action: PayloadAction<{ search: string }>) => {
            state.search = action.payload.search
        },
        updateCategoriesSearchParams: (state: ICategoriesState, action: PayloadAction<ISearchParam[]>) => {
            state.searchParams = action.payload
        },
        setSelectedCategories: (state: ICategoriesState, action: PayloadAction<ICategoryModel[]>) => {
            state.selectedCategories = action.payload
        },
    }
})

/**
 * Return all categories
 */
export const useReturnAllCategories = () => useSelector((state: RootState) => state.categories.categories)

export const updateOrAddCategory = (
    categories: ICategoryModel[],
    newCategory: ICategoryModel
): ICategoryModel[] => {
    return categories.map(category => {
        if (category.id === newCategory.id) {
            // Replace the matching top-level category
            return newCategory;
        } else if (category.sub_categories && category.sub_categories.length > 0) {
            // Recursively update in subcategories
            return {
                ...category,
                sub_categories: updateOrAddCategory(category.sub_categories, newCategory)
            };
        } else {
            return category;
        }
    });
};

export function updateOrAddNestedCategory(
    categories: ICategoryModel[],
    newCategory: ICategoryModel
  ): ICategoryModel[] {
    return categories.map(category => {
      if (category.id === newCategory.id) {
        return newCategory;
      }
  
      if (category.id === newCategory.category_id) {
        const exists = category.sub_categories.some(sub => sub.id === newCategory.id);
        const updatedSubs = exists
          ? category.sub_categories.map(sub =>
              sub.id === newCategory.id ? newCategory : sub
            )
          : [...category.sub_categories, newCategory];
  
        return {
          ...category,
          sub_categories: updateOrAddNestedCategory(updatedSubs, newCategory)
        };
      }
  
      return {
        ...category,
        sub_categories: updateOrAddNestedCategory(category.sub_categories, newCategory)
      };
    });
  }

export function insertOrUpdateCategoryById(
    categories: ICategoryModel[],
    newCategory: ICategoryModel
  ): ICategoryModel[] {
    return categories.map(category => {
      // If we're updating the category directly
      if (category.id === newCategory.id) {
        return newCategory;
      }
  
      // Check if this is the parent category
      if (category.id === newCategory.category_id) {
        const subExists = category.sub_categories.some(sub => sub.id === newCategory.id);
  
        return {
          ...category,
          sub_categories: subExists
            ? category.sub_categories.map(sub =>
                sub.id === newCategory.id ? newCategory : sub
              )
            : [...category.sub_categories, newCategory]
        };
      }
  
      // Recurse deeper into sub_categories
      return {
        ...category,
        sub_categories: insertOrUpdateCategoryById(category.sub_categories, newCategory)
      };
    });
  }

export function findCategoryById(categories: ICategoryModel[], id: number): ICategoryModel | undefined {
    for (const category of categories) {
      if (category.id === id) return category;
      const found = findCategoryById(category.sub_categories, id);
      if (found) return found;
    }
    return undefined;
  }

export function deleteCategoryById(categories: ICategoryModel[], idToDelete: number): ICategoryModel[] {
    return categories
        .filter(category => category.id !== idToDelete)
        .map(category => ({
            ...category,
            sub_categories: deleteCategoryById(category.sub_categories, idToDelete)
        }));
}

/**
 * Retrun single category
 */
export const useReturnSingleSection = (id: number) => {
    const categories = useReturnAllCategories()

    let category: ICategoryModel = EmptyCategoryModel

    if (categories.length > 0){
        categories.map((singleCategory) => {
            if (singleCategory.id === id){
                category = singleCategory
            }
        })
    }

    return category

}

/**
 * Return categories loading status
 */
export const useReturnCategoriesLoadingStatus = () => useSelector((state: RootState) => state.categories.loading)

/**
 * Return categories pagination
 */
export const useReturnCategoriesPagination = () => useSelector((state: RootState) => state.categories.pagination)

/**
 * Return categories search
 */
export const useReturnCategoriesSearch = () => useSelector((state: RootState) => state.categories.search)

/**
 * Return categories search params
 */
export const useReturnCategoriesSearchParams = () => useSelector((state: RootState) => state.categories.searchParams)

/**
 * Return selected categories
 */
export const useReturnSelectedCategories = () => useSelector((state: RootState) => state.categories.selectedCategories)

export const { updateAllCategories, addNewCategory, deleteCategory, updateCategory, updateCategoriesLoadingStatus, updateCategoriesPagination, updateCategoriesSearch, updateCategoriesSearchParams, setSelectedCategories } = categoriesSlice.actions
export default categoriesSlice.reducer